/**
 * Institutions — directApiClient (LF-3216)
 *
 * `revalidatePath` calls dropped — invalidation now happens via React Query
 * at the hook layer (see hooks.tsx + query-dependencies.ts `institution`).
 */

'use client';

import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import {
  Institution,
  InstitutionDuplicateCandidate,
  MatterInstitutionsListResponse,
  InstitutionFilters,
  InstitutionActionResult,
  InstitutionFiltersSchema,
  MatterInstitution,
  UpdateInstitutionInput,
  BatchCreateMatterInstitutionsResponse
} from './types';
import { PaginatedResponse } from '@/lib/types/api';

function toErrorResult<T>(error: unknown): InstitutionActionResult<T> {
  if (error instanceof DirectApiError) {
    return { success: false, error: error.message };
  }
  return {
    success: false,
    error: error instanceof Error ? error.message : 'Request failed'
  };
}

export async function getInstitutionsAction(
  filters?: InstitutionFilters
): Promise<InstitutionActionResult<PaginatedResponse<Institution>>> {
  try {
    if (filters) {
      const validationResult = InstitutionFiltersSchema.safeParse(filters);
      if (!validationResult.success) {
        return {
          success: false,
          error: 'Invalid filters',
          validationErrors: validationResult.error.flatten().fieldErrors
        };
      }
    }

    const params = {
      ...filters,
      page: filters?.page || 1,
      limit: filters?.limit || 10000
    };

    const response = await directApiClient.get<PaginatedResponse<Institution>>(
      '/institutions',
      { params: params as Record<string, unknown> }
    );

    return { success: true, data: response };
  } catch (error) {
    return toErrorResult<PaginatedResponse<Institution>>(error);
  }
}

/**
 * Institutions the firm can already see whose name is the same place as `name`, once
 * case and punctuation are folded (VALE-1422).
 *
 * Asked BEFORE the picker hands its inline-created institution to the parent, because
 * the institution is really created by the inventory-item write — so `create()`'s own
 * 409 arrives after the popover has closed, which is too late to be a choice. This
 * reads the same server-side comparison `create()` enforces with, so the panel cannot
 * disagree with the write that follows it.
 *
 * An empty array means the write will not prompt.
 */
export async function getInstitutionDuplicateCandidatesAction(
  name: string
): Promise<InstitutionActionResult<InstitutionDuplicateCandidate[]>> {
  try {
    const response = await directApiClient.get<InstitutionDuplicateCandidate[]>(
      '/institutions/duplicate-candidates',
      { params: { name } }
    );
    return { success: true, data: response ?? [] };
  } catch (error) {
    return toErrorResult<InstitutionDuplicateCandidate[]>(error);
  }
}

export async function getMatterInstitutionsAction(
  matterId: number
): Promise<InstitutionActionResult<MatterInstitutionsListResponse>> {
  try {
    const institutionsArray = await directApiClient.get<Institution[]>(
      `/matters/${matterId}/institutions`
    );

    const institutions: MatterInstitution[] = (institutionsArray || []).map(
      (inst) => ({
        id: inst.id,
        matterId: matterId,
        institutionId: inst.id,
        institution: inst,
        isPrimary: false,
        notes: undefined,
        createdAt: inst.createdAt || new Date().toISOString(),
        updatedAt: inst.updatedAt || new Date().toISOString()
      })
    );

    const response: MatterInstitutionsListResponse = {
      institutions,
      total: institutions.length
    };

    return { success: true, data: response };
  } catch (error) {
    return toErrorResult<MatterInstitutionsListResponse>(error);
  }
}

/**
 * Batch-add institutions to a matter (Discovery drawer "Add N").
 * Create-or-restore per id; already-linked ids are skipped (no 409).
 * Returns a per-id outcome (`created` / `restored` / `skipped`).
 */
export async function batchAddMatterInstitutionsAction(
  matterId: number,
  institutionIds: number[]
): Promise<InstitutionActionResult<BatchCreateMatterInstitutionsResponse>> {
  try {
    const response =
      await directApiClient.post<BatchCreateMatterInstitutionsResponse>(
        `/matters/${matterId}/institutions/batch`,
        { institutionIds }
      );

    return { success: true, data: response };
  } catch (error) {
    return toErrorResult<BatchCreateMatterInstitutionsResponse>(error);
  }
}

export async function updateInstitutionAction(
  id: number,
  updates: UpdateInstitutionInput
): Promise<InstitutionActionResult<void>> {
  try {
    await directApiClient.put(`/institutions/${id}`, updates);
    return { success: true };
  } catch (error) {
    return toErrorResult<void>(error);
  }
}

/**
 * Remove a custom (firm-owned) institution the firm added itself. Blocked by the
 * backend (409) when a non-draft correspondence has already been sent to it —
 * the resulting `error` string is the reason to show the caller.
 */
export async function deleteInstitutionAction(
  id: number
): Promise<InstitutionActionResult<void>> {
  try {
    await directApiClient.delete(`/institutions/${id}`);
    return { success: true };
  } catch (error) {
    return toErrorResult<void>(error);
  }
}

/**
 * Set the firm's own contact on a **global** institution. The shared
 * institution row is never modified — the address (and, optionally, the named
 * contact) is stored as a per-firm overlay, visible only to this firm. Passing
 * `null` for `email` clears the whole override so correspondence falls back to
 * the global address.
 *
 * `contactName` is optional: an empty string clears any stored name, a name
 * sets it, and `undefined`/`null` leaves it untouched (an email-only write).
 */
export async function setInstitutionFirmContactAction(
  id: number,
  email: string | null,
  contactName?: string | null
): Promise<InstitutionActionResult<void>> {
  try {
    if (email === null) {
      await directApiClient.delete(`/institutions/${id}/firm-contact`);
    } else {
      const body: { email: string; contactName?: string } = { email };
      if (contactName != null) body.contactName = contactName;
      await directApiClient.put(`/institutions/${id}/firm-contact`, body);
    }
    return { success: true };
  } catch (error) {
    return toErrorResult<void>(error);
  }
}
